import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, Output, ViewContainerRef, ViewEncapsulation } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { IMCMapView } from '../../../classes/mcMapView';
import { ILoggingService } from '../../../services/interfaces/ILoggingService';
import { LayerService } from '../../../services/layer.service';
import { LayerCatalogService } from '../../../services/layerCatalog.service';
import { IFeatureLayerCatalog, ILayerCatalogItem, IMapcoreFeatureLayer, IMapServerFolder, LogLevel } from '../../../types';
import { BaseComponent } from '../../base.component';

@Component({
    changeDetection: ChangeDetectionStrategy.Default,
    encapsulation: ViewEncapsulation.ShadowDom,
    selector: 'mc-external-layer-catalog-ng',
    templateUrl: './external-layer-catalog.component.html',
    styleUrls: ['../../sharedStyles/base.scss', './external-layer-catalog.component.scss']
})
export class ExternalLayerCatalogComponent extends BaseComponent implements AfterViewInit {
    _active: any;
    @Input()
    get active() {
        return this._active;
    }
    set active(value: any) {
        if (value) {
            this.onActivate();
        }
        this._active = value;
    }
    @Input() mapViewRef: IMCMapView;
    @Output() readonly onclose: EventEmitter<void> = new EventEmitter<void>();

    isLoading: boolean = false;
    loadingLayers: boolean = false;
    errorLoadingLayers: string = null;
    catalogItems: ILayerCatalogItem[] = [];
    filteredCatalogItems = [];
    searchText: string;
    searchInputForm: FormGroup;
    searchInputControl: FormControl = new FormControl();
    searchTerm: any;
    mapServerFolders: IMapServerFolder[] = [];
    debounceTime = 500;
    selectedLayers: ILayerCatalogItem[] = [];
    featureLayerCatalog: IFeatureLayerCatalog;

    constructor(_vcr: ViewContainerRef,
        private readonly layerService: LayerService,
        private readonly layerCatalogService: LayerCatalogService,
        private readonly _formBuilder: FormBuilder,
        private readonly cdr: ChangeDetectorRef,
        @Inject('LoggingService') public loggingService: ILoggingService) {
        super(_vcr, true);
        this.registerEvent('onclose', this.onclose);
        this.searchInputForm = this._formBuilder.group({
            searchInput: this.searchInputControl
        });
    }

    async ngAfterViewInit() {
        this.mapViewRef = await this.setupMapViewRef(this.mapViewRef);
        this.isReady();
        this.searchInputControl
            .valueChanges
            .pipe(
                debounceTime(this.debounceTime),
                distinctUntilChanged()
            ).subscribe(() => {
                this.filteredCatalogItems = this.filterCatalogItems(this.catalogItems);
                this.cdr.detectChanges();
            });
    }

    async onActivate() {
        await this.ready;
        try {
            this.errorLoadingLayers = null;
            this.isLoading = true;
            this.cdr.detectChanges();
            const rawData = await this.layerCatalogService.getExternalLayers();
            if (rawData) {
                this.featureLayerCatalog = JSON.parse(rawData) as IFeatureLayerCatalog;
                this.processLayerCatalog();
                this.filteredCatalogItems = this.filterCatalogItems(this.catalogItems);
                if (this.featureLayerCatalog.loading && !this.catalogItems.length) {
                    this.errorLoadingLayers = 'External layer catalog is refreshing. This can take a few minutes.';
                } else if (this.featureLayerCatalog.loading && this.catalogItems.length) {
                    this.errorLoadingLayers = 'The layers listed here could be out of date, as the external layer catalog is still being refreshed on the server.';
                    if (this.featureLayerCatalog.updatedOn) {
                        this.errorLoadingLayers += ` The last update was ${new Date(this.featureLayerCatalog.updatedOn).toLocaleString()}.`;
                    }
                }
            } else {
                this.errorLoadingLayers = 'An error occured while retrieving the external layer catalog. Error: No results from API';
            }
        } catch (error) {
            // Occasional server task error, eat error and let user retry
            this.errorLoadingLayers = `An error occured while retrieving the external layer catalog. Error: ${(error as Error).message}`;
        } finally {
            this.isLoading = false;
            this.cdr.detectChanges();
        }
    }

    processLayerCatalog() {
        this.catalogItems = [];
        if (this.featureLayerCatalog.mapServers) {
            for (const mapServer of this.featureLayerCatalog.mapServers) {
                const catalogItem: ILayerCatalogItem = {
                    expanded: false,
                    isSelected: false,
                    name: mapServer.name,
                    layerCatalogItems: []
                };
                if (mapServer.mapServerFolders) {
                    for (const mapServerFolder of mapServer.mapServerFolders) {
                        const serverFolderItem: ILayerCatalogItem = {
                            expanded: false,
                            isSelected: false,
                            name: mapServerFolder.name,
                            layerCatalogItems: []
                        };
                        if (mapServerFolder.services) {
                            for (const service of mapServerFolder.services) {
                                const serviceItem: ILayerCatalogItem = {
                                    expanded: false,
                                    isSelected: false,
                                    name: service.name,
                                    layerCatalogItems: [],
                                    description: service.description || service.serviceDescription
                                };
                                for (const layer of service.layers) {
                                    const layerItem: ILayerCatalogItem = {
                                        expanded: false,
                                        isSelected: false,
                                        name: layer.name,
                                        layer: {
                                            name: layer.name,
                                            url: `${service.url}/${layer.id}`,
                                            id: layer.id
                                        }
                                    };
                                    serviceItem.layerCatalogItems.push(layerItem);
                                }
                                serverFolderItem.layerCatalogItems.push(serviceItem);
                            }
                        }
                        catalogItem.layerCatalogItems.push(serverFolderItem);
                    }
                }
                if (mapServer.services) {
                    for (const service of mapServer.services) {
                        const serviceItem: ILayerCatalogItem = {
                            expanded: false,
                            isSelected: false,
                            name: service.name,
                            layerCatalogItems: [],
                            description: service.description || service.serviceDescription
                        };
                        for (const layer of service.layers) {
                            const layerItem: ILayerCatalogItem = {
                                expanded: false,
                                isSelected: false,
                                name: layer.name,
                                layer: {
                                    name: layer.name,
                                    url: `${service.url}/${layer.id}`,
                                    id: layer.id
                                }
                            };
                            serviceItem.layerCatalogItems.push(layerItem);
                        }
                        catalogItem.layerCatalogItems.push(serviceItem);
                    }
                }
                this.catalogItems.push(catalogItem);
            }
        }

        if (this.catalogItems && this.catalogItems.length) {
            this.catalogItems.forEach(this.configureItemLevel.bind(this));
        }
    }

    private configureItemLevel(item: ILayerCatalogItem) {
        if (!item.layer && item.layerCatalogItems) { // Is a group
            for (let i = item.layerCatalogItems.length - 1; i >= 0; i--) {
                const subItem = item.layerCatalogItems[i];
                this.configureItemLevel(subItem);
                if (!subItem.layer && (subItem.name === item.name || subItem.layerCatalogItems.length === 1)) { // Same name as parent or only 1 item, move it's items up
                    if (subItem.layerCatalogItems.length === 1) { // Only 1 item, use parent's name
                        subItem.layerCatalogItems[0].name = subItem.name;
                    }
                    item.layerCatalogItems.push(...subItem.layerCatalogItems);
                    item.layerCatalogItems.splice(i, 1);
                }
            }
        }
    }

    itemSelectedChanged(catalogItem: ILayerCatalogItem) {
        if (this.selectedLayers && this.selectedLayers.length > 0) {
            for (const [index, item] of this.selectedLayers.entries()) {
                if (!item.isSelected) {
                    this.selectedLayers.splice(index, 1);
                }
            }
        }
        const addToSelected = (item: ILayerCatalogItem) => {
            if (item && item.layer && item.isSelected && !this.selectedLayers.some(sl => sl.layer.url === item.layer.url)) {
                this.selectedLayers.push(item);
            }
            if (item.layerCatalogItems) {
                item.layerCatalogItems.forEach(addToSelected);
            }
        };
        catalogItem.layerCatalogItems.forEach(addToSelected);
    }

    close() {
        this.featureLayerCatalog = null;
        this.catalogItems = [];
        this.filteredCatalogItems = [];
        this.selectedLayers = [];
        this.active = false;
        this.errorLoadingLayers = null;
        this.onclose.emit();
    }

    filterCatalogItems(layerCatalogItems: ILayerCatalogItem[]) {
        const copyOfArray = layerCatalogItems.concat([]);
        const newArray: ILayerCatalogItem[] = [];
        for (const layerCatalogItem of copyOfArray) {
            let currentItem: ILayerCatalogItem = null;
            if (layerCatalogItem.layer) {
                currentItem = layerCatalogItem;
            } else {
                currentItem = { ...layerCatalogItem };
            }
            if ((currentItem.layer && currentItem.layer.id.toString() === this.searchText)
                || (currentItem.isSelected || !this.searchText || this.isNameAMatchToSearchText(currentItem.name))) {
                newArray.push(currentItem);
            }
            if (currentItem.layerCatalogItems) {
                if (newArray.indexOf(currentItem) < 0) {
                    currentItem.layerCatalogItems = this.filterCatalogItems(currentItem.layerCatalogItems);
                    if (this.searchText) {
                        currentItem.expanded = currentItem.layerCatalogItems.length > 0;
                    }
                }
            }
            if (newArray.indexOf(currentItem) < 0 && currentItem.layerCatalogItems && currentItem.layerCatalogItems.length > 0) {
                newArray.push(currentItem);
            }
        }
        return newArray;
    }

    isNameAMatchToSearchText(name: string) {
        let allTermsFound = true;
        for (const term of this.searchText.toLowerCase().split(' ')) {
            allTermsFound = allTermsFound && name.toLowerCase().indexOf(term) >= 0;
        }
        return allTermsFound;
    }

    async addSelectedLayers() {
        const loadingPromises: Promise<IMapcoreFeatureLayer>[] = [];
        for (const selected of this.selectedLayers) {
            loadingPromises.push((async () => {
                try {
                    const layerProps = await this.layerService.getExternalServiceProperties({ url: selected.layer.url });
                    return this.layerService.addExternalLayersToMap(layerProps);
                } catch (error) {
                    this.loggingService.Log(`Error Loading External Layer '${selected.name}'`, error, LogLevel.Error);
                }
            })());
        }
        this.loadingLayers = true;
        this.cdr.detectChanges();
        await Promise.all(loadingPromises);
        this.loadingLayers = false;
        this.close();
        this.cdr.detectChanges();
    }
}
